import { AccessControlAction } from 'app/types/accessControl';

import type {
  ContactPointAction,
  NotificationPolicyAction,
  NotificationTemplateAction,
  TimeIntervalAction,
} from '../hooks/abilities/types';

// Route guards import this module during app startup, so keep it free of React and Alerting runtime dependencies.
export const notificationsPermissions = {
  read: {
    grafana: AccessControlAction.AlertingNotificationsRead,
    external: AccessControlAction.AlertingNotificationsExternalRead,
  },
  create: {
    grafana: AccessControlAction.AlertingNotificationsWrite,
    external: AccessControlAction.AlertingNotificationsExternalWrite,
  },
  update: {
    grafana: AccessControlAction.AlertingNotificationsWrite,
    external: AccessControlAction.AlertingNotificationsExternalWrite,
  },
  delete: {
    grafana: AccessControlAction.AlertingNotificationsWrite,
    external: AccessControlAction.AlertingNotificationsExternalWrite,
  },
};

/** Permissions for the Grafana-managed alertmanager (internal k8s API). */
export const grafanaContactPointPermissions: Record<ContactPointAction, AccessControlAction[]> = {
  'view-contact-point': [notificationsPermissions.read.grafana, AccessControlAction.AlertingReceiversRead],
  'create-contact-point': [notificationsPermissions.create.grafana, AccessControlAction.AlertingReceiversCreate],
  'edit-contact-points': [notificationsPermissions.update.grafana, AccessControlAction.AlertingReceiversWrite],
  'delete-contact-point': [notificationsPermissions.delete.grafana, AccessControlAction.AlertingReceiversDelete],
  'export-contact-point': [notificationsPermissions.read.grafana, AccessControlAction.AlertingReceiversRead],
  'bulk-export-contact-points': [notificationsPermissions.read.grafana, AccessControlAction.AlertingReceiversRead],
  'test-contact-point': [
    notificationsPermissions.update.grafana, // alert.notifications:write — legacy broad permission
    AccessControlAction.AlertingReceiversTest, // deprecated specific action, kept for backward compat
    AccessControlAction.AlertingReceiversTestCreate, // current scoped test action
  ],
};

/** Permissions for external alertmanagers (Mimir, Cortex, Vanilla Alertmanager, etc.). */
export const externalContactPointPermissions: Record<ContactPointAction, AccessControlAction[]> = {
  'view-contact-point': [notificationsPermissions.read.external],
  'create-contact-point': [notificationsPermissions.create.external],
  'edit-contact-points': [notificationsPermissions.update.external],
  'delete-contact-point': [notificationsPermissions.delete.external],
  'export-contact-point': [notificationsPermissions.read.external],
  'bulk-export-contact-points': [], // Not applicable — gated by isGrafanaAlertmanager
  'test-contact-point': [], // Not applicable — the k8s test endpoint is Grafana AM only
};

export const grafanaNotificationPolicyPermissions: Record<NotificationPolicyAction, AccessControlAction[]> = {
  'view-notification-policy-tree': [
    notificationsPermissions.read.grafana,
    AccessControlAction.AlertingRoutesRead,
    AccessControlAction.ActionAlertingManagedRoutesRead,
  ],
  'create-notification-policy': [
    notificationsPermissions.create.grafana,
    AccessControlAction.AlertingRoutesWrite,
    AccessControlAction.ActionAlertingManagedRoutesCreate,
    AccessControlAction.ActionAlertingManagedRoutesWrite,
    AccessControlAction.ActionAlertingManagedRoutesDelete,
  ],
  'update-notification-policy-tree': [
    notificationsPermissions.update.grafana,
    AccessControlAction.AlertingRoutesWrite,
    AccessControlAction.ActionAlertingManagedRoutesCreate,
    AccessControlAction.ActionAlertingManagedRoutesWrite,
    AccessControlAction.ActionAlertingManagedRoutesDelete,
  ],
  'delete-notification-policy': [
    notificationsPermissions.delete.grafana,
    AccessControlAction.AlertingRoutesWrite,
    AccessControlAction.ActionAlertingManagedRoutesCreate,
    AccessControlAction.ActionAlertingManagedRoutesWrite,
    AccessControlAction.ActionAlertingManagedRoutesDelete,
  ],
  'export-notification-policies': [
    notificationsPermissions.read.grafana,
    AccessControlAction.AlertingRoutesRead,
    AccessControlAction.ActionAlertingManagedRoutesRead,
  ],
  'view-autogenerated-policy-tree': [], // gated by isAdmin(), not by RBAC permissions
};

/** Permissions for the Grafana-managed alertmanager (internal k8s API). */
export const grafanaNotificationTemplatePermissions: Record<NotificationTemplateAction, AccessControlAction[]> = {
  'view-notification-template': [notificationsPermissions.read.grafana, AccessControlAction.AlertingTemplatesRead],
  'create-notification-template': [notificationsPermissions.create.grafana, AccessControlAction.AlertingTemplatesWrite],
  'edit-notification-template': [notificationsPermissions.update.grafana, AccessControlAction.AlertingTemplatesWrite],
  'delete-notification-template': [
    notificationsPermissions.delete.grafana,
    AccessControlAction.AlertingTemplatesDelete,
  ],
  'test-notification-template': [
    AccessControlAction.AlertingNotificationsTemplatesTest,
    notificationsPermissions.update.grafana,
  ],
};

/** Permissions for external alertmanagers (Mimir, Cortex, Vanilla Alertmanager, etc.). */
export const externalNotificationTemplatePermissions: Record<NotificationTemplateAction, AccessControlAction[]> = {
  'view-notification-template': [notificationsPermissions.read.external],
  'create-notification-template': [notificationsPermissions.create.external],
  'edit-notification-template': [notificationsPermissions.update.external],
  'delete-notification-template': [notificationsPermissions.delete.external],
  'test-notification-template': [notificationsPermissions.update.external],
};

export const grafanaTimeIntervalPermissions: Record<TimeIntervalAction, AccessControlAction[]> = {
  'view-time-interval': [notificationsPermissions.read.grafana, AccessControlAction.AlertingTimeIntervalsRead],
  'create-time-interval': [notificationsPermissions.create.grafana, AccessControlAction.AlertingTimeIntervalsWrite],
  'update-time-interval': [notificationsPermissions.update.grafana, AccessControlAction.AlertingTimeIntervalsWrite],
  'delete-time-interval': [notificationsPermissions.delete.grafana, AccessControlAction.AlertingTimeIntervalsDelete],
  'export-time-intervals': [notificationsPermissions.read.grafana],
};

function uniquePermissions(permissionSets: AccessControlAction[][]): AccessControlAction[] {
  return Array.from(new Set(permissionSets.flat()));
}

export const PERMISSIONS_CONTACT_POINTS = uniquePermissions(Object.values(grafanaContactPointPermissions));
export const PERMISSIONS_NOTIFICATION_POLICIES = uniquePermissions(Object.values(grafanaNotificationPolicyPermissions));
export const PERMISSIONS_TEMPLATES = uniquePermissions(Object.values(grafanaNotificationTemplatePermissions));
export const PERMISSIONS_TIME_INTERVALS = uniquePermissions(Object.values(grafanaTimeIntervalPermissions));
export const PERMISSIONS_TIME_INTERVALS_READ = [AccessControlAction.AlertingTimeIntervalsRead];
export const PERMISSIONS_TIME_INTERVALS_MODIFY = uniquePermissions([
  grafanaTimeIntervalPermissions['create-time-interval'],
  grafanaTimeIntervalPermissions['update-time-interval'],
]);
